import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { ReactNode } from 'react';
import { Tooltip } from 'src/components';
import { SettingsSection } from 'src/components/SettingsSection';
import { STRIPE_LOGO_URL } from './constants';
import { SectionErrorRow, SectionLoadingRow } from './section-states';
import { StripeConnectedRows } from './stripe-connected-rows';
import { StripeSetupRow } from './stripe-setup-row';
import { StripeLogo } from './styled';
import { useStripeAccountDashboardLoginUrl } from './use-stripe-account-dashboard-login-url';
import { useStripeAccountOnboardStatus } from './use-stripe-account-onboard-status';
import { useStripeAccountOnboardUrl } from './use-stripe-account-onboard-url';

const StripeDashboardLink = () => {
  const { openStripeDashboard, loading } = useStripeAccountDashboardLoginUrl();

  return (
    <Tooltip title="Open Stripe dashboard">
      <IconButton
        size="small"
        aria-label="Open Stripe dashboard"
        onClick={() => void openStripeDashboard()}
        disabled={loading}
      >
        {loading ? <CircularProgress size={16} /> : <ExternalLink size={16} />}
      </IconButton>
    </Tooltip>
  );
};

const StripeContinueOnboardingAction = ({ country }: { country?: string }) => {
  const { redirectToStripeOnboard, loading } = useStripeAccountOnboardUrl();

  return (
    <Button
      variant="contained"
      onClick={() => void redirectToStripeOnboard(country)}
      disabled={loading}
    >
      {loading ? 'Loading...' : 'Continue onboarding'}
    </Button>
  );
};

const SectionTitle = ({ children }: { children?: ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
    <StripeLogo src={STRIPE_LOGO_URL} alt="Stripe" />
    Connect
    {children}
  </Box>
);

/**
 * Stripe Connect group on the Billing page: account state, payout details and
 * the onboarding / dashboard hand-off.
 */
export const StripeConnectSection = () => {
  const {
    detailsSubmitted,
    chargesEnabled,
    country,
    email,
    bankAccount,
    payoutSchedule,
    hasAccount,
    loading,
    error,
    refetch,
  } = useStripeAccountOnboardStatus();

  const isFullySetup = Boolean(detailsSubmitted && chargesEnabled);

  let body;
  let action;
  let title = <SectionTitle />;
  let description = 'Loading your Stripe Express account...';

  if (loading) {
    body = <SectionLoadingRow />;
  } else if (error) {
    body = (
      <SectionErrorRow
        message="We couldn't load your Stripe account information. Please try again."
        onRetry={() => void refetch()}
      />
    );
    description = 'Your Stripe Express payout account.';
  } else if (hasAccount) {
    body = (
      <StripeConnectedRows
        bankAccount={bankAccount}
        country={country}
        email={email}
        payoutSchedule={payoutSchedule}
        isFullySetup={isFullySetup}
      />
    );

    if (isFullySetup) {
      title = (
        <SectionTitle>
          <StripeDashboardLink />
        </SectionTitle>
      );
      description = 'Your Stripe Express account is connected and receiving payments.';
    } else {
      action = <StripeContinueOnboardingAction country={country} />;
      description = 'Finish Stripe onboarding to start receiving membership payments.';
    }
  } else {
    body = <StripeSetupRow />;
    description =
      'Connect a Stripe Express account to receive membership payments directly to your bank account.';
  }

  return (
    <SettingsSection title={title} description={description} action={action}>
      {body}
    </SettingsSection>
  );
};
